import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { HttpErrorResponse } from '@angular/common/http';
import {
  Observable,
  Subject,
  Subscription,
  catchError,
  debounceTime,
  finalize,
  of,
  switchMap,
  takeUntil
} from 'rxjs';
import { ToastrService } from 'ngx-toastr';
import { AuthService } from 'src/app/auth.service';
import {
  ContactAutocompleteResult,
  ContactAutocompleteService
} from 'src/app/services/contacts/contact-autocomplete.service';
import { FscApiService } from './fsc-api.service';
import {
  FscBasis,
  FscBracket,
  FscDefault,
  FscIndex,
  FscPayload,
  FscRate,
  FscSchedule,
  FscSide,
  FormValue,
  LOADS,
  bracketError,
  compatibleSchedules,
  contactPayload,
  isActive,
  numberOrNull,
  percentInput,
  rateId,
  rateMode,
  percentPayload
} from './fsc.models';

type Tab = 'defaults' | FscSide | 'schedules' | 'index-values' | 'bases';
type Kind = 'default' | 'rate' | 'schedule' | 'index' | 'basis' | 'bracket';

interface Option {
  label: string;
  value: string | number | null;
}

interface Field {
  key: string;
  label: string;
  type: 'text' | 'number' | 'date' | 'select' | 'textarea';
  required?: boolean;
  options?: Option[];
  min?: number;
  step?: string;
  help?: string;
}

interface Editor {
  kind: Kind;
  id: string;
  title: string;
  side?: FscSide;
  contact?: string;
  schedule?: FscSchedule;
}

interface ContactState {
  contact: ContactAutocompleteResult | null;
  rows: FscRate[];
  loading: boolean;
  error: string;
  request?: Subscription;
}

interface SideRateState {
  rows: FscRate[];
  loading: boolean;
  error: string;
  request?: Subscription;
}

interface BracketState {
  rows: FscBracket[];
  loading: boolean;
  error: string;
}

@Component({
  selector: 'app-fsc-manager',
  templateUrl: './fsc-manager.component.html',
  styleUrls: ['./fsc-manager.component.scss']
})
export class FscManagerComponent implements OnInit, OnDestroy {
  private readonly destroy$ = new Subject<void>();
  private readonly editorClosed$ = new Subject<void>();
  private detailRequest?: Subscription;
  private readonly search$ = new Subject<{ side: FscSide; query: string }>();
  private permissions = new Set<string>();
  readonly tabs: { id: Tab; label: string; icon: string }[] = [
    { id: 'defaults', label: 'Defaults', icon: 'fa-globe' },
    { id: 'customer', label: 'Customers', icon: 'fa-building' },
    { id: 'carrier', label: 'Carriers', icon: 'fa-truck' },
    { id: 'schedules', label: 'Schedules', icon: 'fa-list-ol' },
    { id: 'index-values', label: 'Index values', icon: 'fa-chart-line' },
    { id: 'bases', label: 'Bases', icon: 'fa-layer-group' }
  ];
  readonly loads = LOADS;
  readonly areas = [
    { label: 'Canada', value: 1 },
    { label: 'USA', value: 2 }
  ];
  readonly sources: Option[] = [
    { label: 'FCA', value: 1 },
    { label: 'DOE', value: 2 }
  ];
  readonly pageSize = 25;
  tab: Tab = 'defaults';
  theme =
    localStorage.getItem('ulaap-fsc-theme') ||
    localStorage.getItem('ulaap-rate-manager-theme') ||
    'dark';
  defaults: FscDefault[] = [];
  schedules: FscSchedule[] = [];
  indexes: FscIndex[] = [];
  bases: FscBasis[] = [];
  loading: Partial<Record<Tab, boolean>> = {};
  errors: Partial<Record<Tab, string>> = {};
  contacts: Record<FscSide, ContactState> = {
    customer: { contact: null, rows: [], loading: false, error: '' },
    carrier: { contact: null, rows: [], loading: false, error: '' }
  };
  sideRates: Record<FscSide, SideRateState> = {
    customer: { rows: [], loading: false, error: '' },
    carrier: { rows: [], loading: false, error: '' }
  };
  contactQuery = '';
  contactOptions: ContactAutocompleteResult[] = [];
  contactSearchError = '';
  searching = false;
  searchOpen = false;
  filter = '';
  status = 'all';
  page = 0;
  expandedRate = '';
  expandedSchedules = new Set<string>();
  bracketStates: Partial<Record<string, BracketState>> = {};
  editor: Editor | null = null;
  form = new FormGroup<Record<string, FormControl<FormValue>>>({});
  fields: Field[] = [];
  saving = false;
  detailLoading = false;
  editorError = '';
  pendingDeactivate = false;

  constructor(
    private readonly api: FscApiService,
    private readonly auth: AuthService,
    private readonly autocomplete: ContactAutocompleteService,
    private readonly toast: ToastrService
  ) {}

  ngOnInit(): void {
    this.auth
      .ensureCurrentUserLoaded()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: user => (this.permissions = new Set(user?.permissions || [])),
        error: () => (this.errors['defaults'] = 'Could not load your account permissions.')
      });
    this.search$
      .pipe(
        debounceTime(250),
        switchMap(({ side, query }) =>
          query.length < 3
            ? of({ side, query, rows: [] as ContactAutocompleteResult[], error: '' })
            : this.autocomplete.search(`${side}_contact_list`, query).pipe(
                switchMap(rows => of({ side, query, rows, error: '' })),
                catchError(() =>
                  of({
                    side,
                    query,
                    rows: [] as ContactAutocompleteResult[],
                    error: 'Contact search failed. Try again.'
                  })
                )
              )
        ),
        takeUntil(this.destroy$)
      )
      .subscribe(result => {
        if (this.tab !== result.side || this.contactQuery !== result.query) return;
        this.contactOptions = result.rows;
        this.contactSearchError = result.error;
        this.searching = false;
      });
    this.loadDefaults();
    this.loadSchedules();
    this.loadBases();
    this.loadIndexes();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.editorClosed$.next();
    this.editorClosed$.complete();
    this.detailRequest?.unsubscribe();
    Object.values(this.sideRates).forEach(state => state.request?.unsubscribe());
  }

  get canEdit(): boolean {
    return this.permissions.has('glebus_rate_manager_edit') || this.permissions.has('rates.manage');
  }

  get canDelete(): boolean {
    return (
      this.permissions.has('glebus_rate_manager_delete') || this.permissions.has('rates.manage')
    );
  }

  get side(): FscSide {
    return this.tab === 'customer' ? 'customer' : 'carrier';
  }

  get isContactTab(): boolean {
    return this.tab === 'customer' || this.tab === 'carrier';
  }

  get contactState(): ContactState {
    return this.contacts[this.side];
  }

  get sideRateState(): SideRateState {
    return this.sideRates[this.side];
  }

  get busy(): boolean {
    return this.saving || this.detailLoading;
  }

  get currentError(): string {
    return this.isContactTab ? this.contactState.error : this.errors[this.tab] || '';
  }

  get currentLoading(): boolean {
    return this.isContactTab ? this.contactState.loading : !!this.loading[this.tab];
  }

  get activeTitle(): string {
    return this.tabs.find(t => t.id === this.tab)?.label || '';
  }

  get mode(): FormValue {
    return this.form.controls['mode']?.value || 'fixed';
  }

  get visibleFields(): Field[] {
    return this.fields.filter(
      field =>
        this.editor?.kind !== 'rate' ||
        ((field.key !== 'offset_basis' || this.mode === 'offset') &&
          (!LOADS.includes(field.key as (typeof LOADS)[number]) || this.mode !== 'schedule'))
    );
  }

  get filteredCount(): number {
    return this.filteredRows().length;
  }

  get pageCount(): number {
    return Math.max(1, Math.ceil(this.filteredCount / this.pageSize));
  }

  get defaultRows(): FscDefault[] {
    return this.paged(this.defaults);
  }

  get rateRows(): FscRate[] {
    return this.paged(this.contactState.rows);
  }

  get sideRateRows(): FscRate[] {
    return this.sideRateState.rows;
  }

  get scheduleRows(): FscSchedule[] {
    return this.paged(this.schedules);
  }

  get indexRows(): FscIndex[] {
    return this.paged(this.indexes);
  }

  get basisRows(): FscBasis[] {
    return this.paged(this.bases);
  }

  get editorResource():
    | 'defaults'
    | 'contact-rates'
    | 'schedules'
    | 'index-values'
    | 'bases'
    | 'brackets' {
    const resources = {
      default: 'defaults',
      rate: 'contact-rates',
      schedule: 'schedules',
      index: 'index-values',
      basis: 'bases',
      bracket: 'brackets'
    } as const;
    return resources[this.editor!.kind];
  }

  toggleTheme(): void {
    this.theme = this.theme === 'dark' ? 'light' : 'dark';
    localStorage.setItem('ulaap-fsc-theme', this.theme);
  }

  setTab(tab: Tab): void {
    if (tab === this.tab || !this.closeEditor()) return;
    this.detailRequest?.unsubscribe();
    this.detailLoading = false;
    this.tab = tab;
    this.page = 0;
    this.filter = '';
    this.status = 'all';
    this.expandedRate = '';
    this.contactQuery = this.isContactTab ? this.contactState.contact?.label || '' : '';
    this.contactOptions = [];
    this.contactSearchError = '';
    this.searching = false;
    this.searchOpen = false;
    if (this.isContactTab) this.loadSideRates(this.side);
  }

  filterChanged(): void {
    this.page = 0;
  }

  changePage(delta: number): void {
    this.page = Math.max(0, Math.min(this.pageCount - 1, this.page + delta));
  }

  refresh(): void {
    if (this.isContactTab) {
      this.loadSideRates(this.side);
      this.loadRates(this.side);
    }
    else if (this.tab === 'defaults') this.loadDefaults();
    else if (this.tab === 'schedules') this.loadSchedules();
    else if (this.tab === 'index-values') this.loadIndexes();
    else this.loadBases();
  }

  loadDefaults(): void {
    this.read('defaults', this.api.defaults(), rows => (this.defaults = rows));
  }

  loadSchedules(): void {
    this.read('schedules', this.api.schedules(), rows => (this.schedules = rows));
  }

  loadIndexes(): void {
    this.read('index-values', this.api.indexes(), rows => (this.indexes = rows));
  }

  loadBases(): void {
    this.read('bases', this.api.bases(), rows => (this.bases = rows));
  }

  private read<T>(key: Tab, request: Observable<T>, accept: (data: T) => void): void {
    if (this.loading[key]) return;
    this.loading[key] = true;
    this.errors[key] = '';
    request
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => (this.loading[key] = false))
      )
      .subscribe({
        next: rows => {
          accept(rows);
          this.page = Math.min(this.page, this.pageCount - 1);
        },
        error: error => (this.errors[key] = this.errorMessage(error))
      });
  }

  searchContact(): void {
    this.contactOptions = [];
    this.searchOpen = true;
    this.contactSearchError = '';
    this.searching = this.contactQuery.trim().length >= 3;
    this.search$.next({ side: this.side, query: this.contactQuery });
  }

  chooseContact(contact: ContactAutocompleteResult): void {
    if (!this.closeEditor()) return;
    this.contactState.contact = contact;
    this.contactQuery = contact.label;
    this.searchOpen = false;
    this.contactOptions = [];
    this.searching = false;
    this.expandedRate = '';
    this.page = 0;
    this.loadRates(this.side);
  }

  loadRates(side: FscSide): void {
    const state = this.contacts[side];
    state.request?.unsubscribe();
    state.rows = [];
    state.error = '';
    if (!state.contact) return;
    const uuid = state.contact.value;
    state.loading = true;
    state.request = this.api
      .rates(side, uuid)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => (state.loading = false))
      )
      .subscribe({
        next: rows => {
          if (state.contact?.value === uuid) state.rows = rows;
        },
        error: error => {
          if (state.contact?.value === uuid) state.error = this.errorMessage(error);
        }
      });
  }

  loadSideRates(side: FscSide): void {
    const state = this.sideRates[side];
    state.request?.unsubscribe();
    state.error = '';
    state.loading = true;
    state.request = this.api
      .side(side)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => (state.loading = false))
      )
      .subscribe({
        next: rows => (state.rows = rows),
        error: error => {
          state.rows = [];
          state.error = this.errorMessage(error);
        }
      });
  }

  toggleRate(row: FscRate): void {
    const id = rateId(row, this.side);
    this.expandedRate = this.expandedRate === id ? '' : id;
  }

  schedulesFor(row: FscRate): FscSchedule[] {
    return compatibleSchedules(
      this.schedules,
      this.side,
      this.contactState.contact?.value || '',
      rateId(row, this.side)
    );
  }

  toggleSchedule(row: FscSchedule): void {
    const id = row.fuel_surcharge_schedule_uuid;
    if (this.expandedSchedules.has(id)) this.expandedSchedules.delete(id);
    else {
      this.expandedSchedules.add(id);
      this.loadBrackets(row);
    }
  }

  loadBrackets(row: FscSchedule): void {
    const id = row.fuel_surcharge_schedule_uuid;
    const state =
      this.bracketStates[id] || (this.bracketStates[id] = { rows: [], loading: false, error: '' });
    if (state.loading) return;
    state.loading = true;
    state.error = '';
    this.api
      .brackets(id)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => (state.loading = false))
      )
      .subscribe({
        next: rows => (state.rows = rows),
        error: error => (state.error = this.errorMessage(error))
      });
  }

  newRecord(): void {
    if (this.isContactTab) this.editRate();
    else if (this.tab === 'defaults') this.editDefault();
    else if (this.tab === 'schedules') this.editSchedule();
    else if (this.tab === 'index-values') this.editIndex();
    else this.editBasis();
  }

  editDefault(row?: FscDefault): void {
    if (
      !this.open({
        kind: 'default',
        id: row?.fuel_surcharge_default_uuid || '',
        title: row ? 'Edit default' : 'New default'
      })
    )
      return;
    this.fields = [
      this.select(
        'effective_area',
        'Area',
        [{ label: 'All areas', value: null }, ...this.areas],
        false
      ),
      this.select(
        'rate_source',
        'Source',
        [{ label: 'Unspecified', value: null }, ...this.sources],
        false
      ),
      ...this.dates(),
      ...LOADS.map(load => this.numeric(load, `${this.loadLabel(load)} %`)),
      this.notes()
    ];
    this.buildForm({
      ...this.common(row),
      effective_area: row ? row.effective_area : 1,
      rate_source: row ? row.rate_source : 1,
      ...Object.fromEntries(LOADS.map(load => [load, percentInput(row?.[`default_${load}_rate`])]))
    });
  }

  editRate(row?: FscRate): void {
    const contact = this.contactState.contact;
    if (
      !contact ||
      !this.open({
        kind: 'rate',
        id: row ? rateId(row, this.side) : '',
        title: row ? 'Edit FSC rule' : 'New FSC rule',
        side: this.side,
        contact: contact.value
      })
    )
      return;
    const prefix = this.side === 'carrier' ? 'cost' : 'chargeable';
    const mode = row ? rateMode(row, this.side) : 'fixed';
    this.fields = [
      this.select('mode', 'Calculation', [
        { label: 'Fixed percentage', value: 'fixed' },
        { label: 'Default × multiplier', value: 'offset' },
        { label: 'Index-based schedule', value: 'schedule' }
      ]),
      ...this.dates(),
      this.select('offset_basis', 'Default source', this.sources),
      ...LOADS.map(load => this.numeric(load, this.loadLabel(load))),
      this.notes()
    ];
    this.buildForm({
      ...this.common(row),
      mode,
      offset_basis: row?.offset_basis ?? 1,
      ...Object.fromEntries(
        LOADS.map(load => [
          load,
          mode === 'offset'
            ? numberOrNull(row?.[`${prefix}_${load}_offset_multiplier`])
            : percentInput(row?.[`${prefix}_${load}_rate`])
        ])
      )
    });
    this.form.controls['mode'].valueChanges
      .pipe(takeUntil(this.editorClosed$), takeUntil(this.destroy$))
      .subscribe(() => {
        for (const load of LOADS) this.form.controls[load].setValue(null);
      });
  }

  editSchedule(row?: FscSchedule, rate?: FscRate): void {
    if (row) {
      if (!this.closeEditor()) return;
      this.detailRequest?.unsubscribe();
      this.detailLoading = true;
      this.detailRequest = this.api
        .schedule(row.fuel_surcharge_schedule_uuid)
        .pipe(
          takeUntil(this.destroy$),
          finalize(() => (this.detailLoading = false))
        )
        .subscribe({
          next: full => this.openSchedule(full),
          error: error => this.toast.error(this.errorMessage(error))
        });
    } else this.openSchedule(undefined, rate);
  }

  private openSchedule(row?: FscSchedule, rate?: FscRate): void {
    const side = row?.side || (this.isContactTab ? this.side : 'carrier');
    const contact = rate ? this.contactState.contact?.value || '' : '';
    if (
      !this.open({
        kind: 'schedule',
        id: row?.fuel_surcharge_schedule_uuid || '',
        title: row ? 'Edit schedule' : 'New schedule',
        side
      })
    )
      return;
    const basisOptions = this.basisOptions(row?.schedule_basis);
    this.fields = [
      { key: 'schedule_name', label: 'Schedule name', type: 'text', required: true },
      this.select('side', 'Side', [
        { label: 'Carrier', value: 'carrier' },
        { label: 'Customer', value: 'customer' }
      ]),
      this.select(
        'load_type',
        'Load type',
        ['LTL', 'TL', 'INTERMODAL'].map(value => ({ label: value, value }))
      ),
      this.select('schedule_basis', 'Index basis', basisOptions),
      this.select(
        'effective_area',
        'Index area',
        [{ label: 'Shipment area', value: null }, ...this.areas],
        false
      ),
      this.select(
        'currency_type',
        'Currency',
        [
          { label: 'Unspecified', value: null },
          { label: 'USD', value: 'USD' },
          { label: 'CAD', value: 'CAD' }
        ],
        false
      ),
      ...this.dates(),
      {
        ...this.select(
          'contact_uuid',
          'Applies to',
          this.scheduleContacts(
            side,
            row
              ? side === 'carrier'
                ? row.carrier_contact_uuid
                : row.customer_contact_uuid
              : contact
          ),
          false
        ),
        help: 'For another contact, open its Customers or Carriers tab and add a schedule from its rule.'
      },
      this.select(
        'rate_uuid',
        'Use this schedule for',
        this.scheduleRules(
          side,
          row
            ? side === 'carrier'
              ? row.carrier_contact_uuid
              : row.customer_contact_uuid
            : contact,
          row
            ? side === 'carrier'
              ? row.fuel_surcharge_cost_rate_uuid
              : row.fuel_surcharge_chargeable_rate_uuid
            : rate
              ? rateId(rate, side)
              : ''
        ),
        false
      ),
      this.notes()
    ];
    this.buildForm({
      ...this.common(row || rate),
      schedule_name: row?.schedule_name || '',
      side,
      load_type: row?.load_type || 'LTL',
      schedule_basis: row?.schedule_basis || (basisOptions[0]?.value ?? ''),
      effective_area: row?.effective_area ?? null,
      currency_type: row ? row.currency_type : 'USD',
      contact_uuid: row
        ? (side === 'carrier' ? row.carrier_contact_uuid : row.customer_contact_uuid) || ''
        : contact,
      rate_uuid: row
        ? (side === 'carrier'
            ? row.fuel_surcharge_cost_rate_uuid
            : row.fuel_surcharge_chargeable_rate_uuid) || ''
        : rate
          ? rateId(rate, side)
          : ''
    });
    this.form.controls['side'].valueChanges
      .pipe(takeUntil(this.editorClosed$), takeUntil(this.destroy$))
      .subscribe(() => {
        this.form.controls['contact_uuid'].setValue('');
        this.form.controls['rate_uuid'].setValue('');
        this.fields.find(f => f.key === 'contact_uuid')!.options = this.scheduleContacts(
          this.form.controls['side'].value as FscSide
        );
        this.fields.find(f => f.key === 'rate_uuid')!.options = this.scheduleRules(
          this.form.controls['side'].value as FscSide,
          ''
        );
      });
    this.form.controls['contact_uuid'].valueChanges
      .pipe(takeUntil(this.editorClosed$), takeUntil(this.destroy$))
      .subscribe(value => {
        this.form.controls['rate_uuid'].setValue('');
        this.fields.find(f => f.key === 'rate_uuid')!.options = this.scheduleRules(
          this.form.controls['side'].value as FscSide,
          String(value || '')
        );
      });
  }

  private scheduleContacts(side: FscSide, existing?: string | null): Option[] {
    const selected = this.contacts[side].contact;
    const options: Option[] = [{ label: 'All ' + side + 's (shared)', value: '' }];
    if (selected) options.push({ label: selected.label, value: selected.value });
    if (existing && existing !== selected?.value)
      options.push({
        label: 'Linked ' + side + ' (' + existing.slice(0, 8) + ')',
        value: existing
      });
    return options;
  }

  private scheduleRules(
    side: FscSide,
    contact?: string | null,
    existing?: string | null
  ): Option[] {
    const state = this.contacts[side];
    const options: Option[] = [
      {
        label: contact ? 'Default for this ' + side : 'Shared default for all ' + side + 's',
        value: ''
      }
    ];
    if (contact && state.contact?.value === contact)
      for (const rate of state.rows.filter(r => isActive(r.uses_schedule)))
        options.push({
          label:
            'Only rule: ' + this.dateRange(rate) + (isActive(rate.active) ? '' : ' (inactive)'),
          value: rateId(rate, side)
        });
    if (existing && !options.some(o => o.value === existing))
      options.push({ label: 'Only linked rule (' + existing.slice(0, 8) + ')', value: existing });
    return options;
  }

  editIndex(row?: FscIndex): void {
    if (
      !this.open({
        kind: 'index',
        id: row?.fuel_surcharge_index_value_uuid || '',
        title: row ? 'Edit index value' : 'New index value'
      })
    )
      return;
    this.fields = [
      this.select('schedule_basis', 'Index basis', this.basisOptions(row?.schedule_basis)),
      this.select(
        'effective_area',
        'Area',
        [{ label: 'All areas', value: null }, ...this.areas],
        false
      ),
      ...this.dates(),
      { ...this.numeric('index_value', 'Index value'), required: true },
      { key: 'source_name', label: 'Source name', type: 'text' },
      { key: 'source_url', label: 'Source URL', type: 'text' },
      this.notes()
    ];
    this.buildForm({
      ...this.common(row),
      schedule_basis: row?.schedule_basis || this.bases[0]?.schedule_basis || '',
      effective_area: row?.effective_area ?? null,
      index_value: numberOrNull(row?.index_value),
      source_name: row?.source_name || '',
      source_url: row?.source_url || ''
    });
  }

  editBasis(row?: FscBasis): void {
    if (
      !this.open({
        kind: 'basis',
        id: row?.schedule_basis || '',
        title: row ? 'Edit basis' : 'New basis'
      })
    )
      return;
    this.fields = [
      {
        key: 'schedule_basis',
        label: 'Basis code',
        type: 'text',
        required: true,
        help: 'For example, DOE_DIESEL_INDEX. Renaming also updates schedules and index values using this basis.'
      },
      { key: 'label', label: 'Display name', type: 'text' }
    ];
    this.buildForm({ schedule_basis: row?.schedule_basis || '', label: row?.label || '' });
  }

  editBracket(schedule: FscSchedule, row?: FscBracket): void {
    if (
      !this.open({
        kind: 'bracket',
        id: row?.fuel_surcharge_schedule_bracket_uuid || '',
        title: row ? 'Edit bracket' : 'New bracket',
        schedule
      })
    )
      return;
    this.fields = [
      { ...this.numeric('min_index', 'Minimum index'), min: undefined, required: true },
      {
        ...this.numeric('max_index', 'Maximum index'),
        min: undefined,
        help: 'Leave blank for no upper limit. Both ends of a range are inclusive.'
      },
      {
        ...this.numeric(
          'rate_amount',
          `Rate amount (${schedule.currency_type || 'currency unspecified'})`
        ),
        required: true
      },
      this.select('rate_unit', 'Rate unit', [
        { label: 'Per mile', value: 'PER_MILE' },
        { label: 'Flat amount', value: 'FLAT' },
        ...(['DOLLARS', 'AMOUNT'].includes(row?.rate_unit || '')
          ? [{ label: 'Flat amount (' + row!.rate_unit + ')', value: row!.rate_unit }]
          : row?.rate_unit && !['PER_MILE', 'FLAT'].includes(row.rate_unit)
            ? [{ label: 'Unsupported: ' + row.rate_unit, value: row.rate_unit }]
            : [])
      ]),
      { ...this.numeric('sort_order', 'Sort order'), min: undefined, step: '1' },
      this.notes()
    ];
    this.buildForm({
      min_index: numberOrNull(row?.min_index),
      max_index: numberOrNull(row?.max_index),
      rate_amount: numberOrNull(row?.rate_amount),
      rate_unit: row?.rate_unit || 'PER_MILE',
      sort_order: numberOrNull(row?.sort_order) ?? 0,
      active: row ? isActive(row.active) : true,
      note: row?.note || ''
    });
  }

  closeEditor(): boolean {
    if (this.saving) return false;
    if (this.editor && this.form.dirty && !window.confirm('Discard your unsaved changes?'))
      return false;
    this.editorClosed$.next();
    this.editor = null;
    this.editorError = '';
    this.pendingDeactivate = false;
    return true;
  }

  private open(editor: Editor): boolean {
    if (!this.closeEditor()) return false;
    this.editor = editor;
    this.editorError = '';
    this.pendingDeactivate = false;
    return true;
  }

  save(): void {
    if (!this.editor || this.saving || !this.canEdit) return;
    this.form.markAllAsTouched();
    const raw = this.form.getRawValue();
    this.editorError = this.validate(raw);
    if (this.editorError) return;
    const editor = this.editor;
    const payload = this.payload(raw);
    this.saving = true;
    this.api
      .save<FscDefault | FscRate | FscSchedule | FscIndex | FscBasis | FscBracket>(
        this.editorResource,
        payload,
        {
          id: editor.id,
          side: editor.side,
          contact_uuid: editor.contact,
          schedule_uuid: editor.schedule?.fuel_surcharge_schedule_uuid
        }
      )
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => (this.saving = false))
      )
      .subscribe({
        next: saved => {
          this.form.markAsPristine();
          this.editor = null;
          this.toast.success('Saved.');
          this.reloadAfter(editor);
          if (editor.kind === 'schedule') {
            const schedule = saved as FscSchedule;
            this.expandedSchedules.add(schedule.fuel_surcharge_schedule_uuid);
            this.loadBrackets(schedule);
          }
        },
        error: error => (this.editorError = this.errorMessage(error))
      });
  }

  deactivate(): void {
    if (!this.editor?.id || !this.canDelete || this.saving) return;
    const editor = this.editor;
    this.saving = true;
    this.editorError = '';
    this.api
      .deactivate(this.editorResource, {
        id: editor.id,
        side: editor.side,
        contact_uuid: editor.contact,
        schedule_uuid: editor.schedule?.fuel_surcharge_schedule_uuid
      })
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => (this.saving = false))
      )
      .subscribe({
        next: () => {
          this.form.markAsPristine();
          this.editor = null;
          this.toast.success(editor.kind === 'basis' ? 'Basis removed.' : 'Deactivated.');
          this.reloadAfter(editor);
        },
        error: error => (this.editorError = this.errorMessage(error))
      });
  }

  private reloadAfter(editor: Editor): void {
    if (editor.kind === 'default') this.loadDefaults();
    if (editor.kind === 'rate') {
      this.loadRates(editor.side!);
      this.loadSideRates(editor.side!);
    }
    if (editor.kind === 'schedule') this.loadSchedules();
    if (editor.kind === 'index') this.loadIndexes();
    if (editor.kind === 'basis') {
      this.loadBases();
      this.loadSchedules();
      this.loadIndexes();
    }
    if (editor.kind === 'bracket') {
      this.loadBrackets(editor.schedule!);
      this.loadSchedules();
    }
  }

  private payload(raw: FscPayload): FscPayload {
    const kind = this.editor!.kind;
    if (kind === 'rate') return contactPayload(raw, this.editor!.side!);
    const payload: FscPayload = { ...raw };
    for (const key of Object.keys(payload)) if (payload[key] === '') payload[key] = null;
    if (kind === 'default') {
      for (const load of LOADS) {
        payload[`default_${load}_rate`] = percentPayload(raw[load]);
        delete payload[load];
      }
    }
    if (kind === 'schedule') {
      const side = raw['side'];
      payload['carrier_contact_uuid'] = side === 'carrier' ? raw['contact_uuid'] || null : null;
      payload['customer_contact_uuid'] = side === 'customer' ? raw['contact_uuid'] || null : null;
      payload['fuel_surcharge_cost_rate_uuid'] =
        side === 'carrier' ? raw['rate_uuid'] || null : null;
      payload['fuel_surcharge_chargeable_rate_uuid'] =
        side === 'customer' ? raw['rate_uuid'] || null : null;
      delete payload['contact_uuid'];
      delete payload['rate_uuid'];
      // Existing brackets are saved through their own endpoints; never replace their IDs here.
    }
    return payload;
  }

  private validate(raw: FscPayload): string {
    if (this.form.invalid) return 'Check the highlighted fields before saving.';
    if (raw['effective_end'] && String(raw['effective_end']) < String(raw['effective_start']))
      return 'End date cannot be before start date.';
    const kind = this.editor!.kind;
    if (
      (kind === 'default' || (kind === 'rate' && raw['mode'] !== 'schedule')) &&
      LOADS.every(load => numberOrNull(raw[load]) === null)
    )
      return 'Enter at least one rate or multiplier. Zero is allowed; blank means not configured.';
    if (kind === 'schedule') {
      for (const key of ['contact_uuid', 'rate_uuid'])
        if (raw[key] && !/^[0-9a-f]{8}(-[0-9a-f]{4}){3}-[0-9a-f]{12}$/i.test(String(raw[key])))
          return 'Contact and rule IDs must be valid UUIDs, or blank.';
      if (raw['rate_uuid'] && !raw['contact_uuid'])
        return 'Provide the contact for this specific FSC rule.';
    }
    if (kind === 'basis' && !/^[A-Za-z0-9_ -]{1,80}$/.test(String(raw['schedule_basis'])))
      return 'Use letters, numbers, spaces, underscores or hyphens for the basis code (80 characters maximum).';
    if (kind === 'bracket') {
      if (!['PER_MILE', 'FLAT', 'DOLLARS', 'AMOUNT'].includes(String(raw['rate_unit'])))
        return 'Choose a supported rate unit before saving.';
      if (!Number.isInteger(Number(raw['sort_order']))) return 'Sort order must be a whole number.';
      return bracketError(
        raw,
        this.bracketStates[this.editor!.schedule!.fuel_surcharge_schedule_uuid]?.rows || [],
        this.editor!.id
      );
    }
    return '';
  }

  private buildForm(values: FscPayload): void {
    const controls: Record<string, FormControl<FormValue>> = {};
    for (const [key, value] of Object.entries(values)) {
      const field = this.fields.find(f => f.key === key);
      const validators = [];
      if (field?.required) validators.push(Validators.required);
      if (field?.min !== undefined) validators.push(Validators.min(field.min));
      controls[key] = new FormControl<FormValue>(
        field?.type === 'select' &&
        typeof field.options?.find(o => String(o.value) === String(value))?.value === 'number'
          ? Number(value)
          : value,
        validators
      );
    }
    this.form = new FormGroup(controls);
    if (!this.canEdit) this.form.disable();
  }

  private common(row?: {
    effective_start?: string | null;
    effective_end?: string | null;
    active?: unknown;
    note?: string | null;
  }): FscPayload {
    return {
      effective_start: row?.effective_start?.slice(0, 10) || this.today(),
      effective_end: row?.effective_end?.slice(0, 10) || '',
      active: row ? isActive(row.active) : true,
      note: row?.note || ''
    };
  }

  private dates(): Field[] {
    return [
      { key: 'effective_start', label: 'Effective start', type: 'date', required: true },
      {
        key: 'effective_end',
        label: 'Effective end',
        type: 'date',
        help: 'Leave blank for no end date.'
      }
    ];
  }

  private numeric(key: string, label: string): Field {
    return { key, label, type: 'number', min: 0, step: 'any' };
  }

  private select(key: string, label: string, options: Option[], required = true): Field {
    return { key, label, type: 'select', options, required };
  }

  private notes(): Field {
    return { key: 'note', label: 'Notes', type: 'textarea' };
  }

  private basisOptions(existing?: string): Option[] {
    const options = this.bases.map(row => ({
      value: row.schedule_basis,
      label: row.label || row.schedule_basis
    }));
    if (existing && !options.some(o => o.value === existing))
      options.push({ value: existing, label: existing });
    return options;
  }

  fieldLabel(field: Field): string {
    return this.editor?.kind === 'rate' && LOADS.includes(field.key as (typeof LOADS)[number])
      ? `${field.label} ${this.mode === 'offset' ? 'multiplier' : '%'}`
      : field.label;
  }

  fieldHelp(field: Field): string | undefined {
    if (this.editor?.kind !== 'schedule' || field.key !== 'rate_uuid') return field.help;
    const side = this.form.controls['side'].value;
    const limits = " Only applies within this schedule's dates and load type.";
    if (this.form.controls['rate_uuid'].value) {
      return (
        'Used only for the selected index-based FSC rule. Takes priority over the default schedule for this ' +
        side +
        '.' +
        limits
      );
    }
    if (this.form.controls['contact_uuid'].value) {
      return (
        'Used by this ' +
        side +
        "'s index-based FSC rules unless a matching schedule is linked to a specific rule." +
        limits
      );
    }
    return (
      'Used by index-based FSC rules across all ' +
      side +
      's when no ' +
      side +
      '-specific schedule matches.' +
      limits
    );
  }

  isInvalid(key: string): boolean {
    const control = this.form.controls[key];
    return !!control?.invalid && control.touched;
  }

  private filteredRows(): unknown[] {
    return this.filtered<unknown>(
      this.tab === 'defaults'
        ? this.defaults
        : this.tab === 'schedules'
          ? this.schedules
          : this.tab === 'index-values'
            ? this.indexes
            : this.tab === 'bases'
              ? this.bases
              : this.contactState.rows
    );
  }

  private filtered<T>(rows: T[]): T[] {
    const query = this.filter.toLowerCase().trim();
    return rows.filter(row => {
      const record = row as Record<string, unknown>;
      const text = Object.values(record).join(' ').toLowerCase();
      const area = this.area(record['effective_area']).toLowerCase();
      const source = this.source(record['rate_source'] ?? record['offset_basis']).toLowerCase();
      return (
        (!query || `${text} ${area} ${source}`.includes(query)) &&
        (this.tab === 'bases' ||
          this.status === 'all' ||
          isActive(record['active']) === (this.status === 'active'))
      );
    });
  }

  private paged<T>(rows: T[]): T[] {
    return this.filtered(rows).slice(this.page * this.pageSize, (this.page + 1) * this.pageSize);
  }

  currentDefault(area: number): FscDefault | undefined {
    const today = this.today();
    return this.defaults
      .filter(
        row =>
          Number(row.effective_area) === area &&
          Number(row.rate_source) === area &&
          isActive(row.active) &&
          (!row.effective_start || row.effective_start.slice(0, 10) <= today) &&
          (!row.effective_end || row.effective_end.slice(0, 10) >= today)
      )
      .sort((a, b) => String(b.effective_start).localeCompare(String(a.effective_start)))[0];
  }

  today(): string {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
  }

  active = isActive;
  rateId = rateId;
  modeOf = rateMode;

  defaultValue(row: FscDefault, load: (typeof LOADS)[number]): string {
    return this.percent(row[`default_${load}_rate`]);
  }

  percent(value: unknown): string {
    const number = percentInput(value);
    return number === null
      ? '—'
      : `${number.toLocaleString('en-US', { maximumFractionDigits: 4 })}%`;
  }

  number(value: unknown): string {
    const number = numberOrNull(value);
    return number === null ? '—' : number.toLocaleString('en-US', { maximumFractionDigits: 6 });
  }

  area(value: unknown): string {
    return value === null || value === undefined || value === ''
      ? 'All areas'
      : this.areas.find(o => o.value === Number(value))?.label || String(value);
  }

  source(value: unknown): string {
    return this.sources.find(o => o.value === Number(value))?.label || '—';
  }

  basis(value: string): string {
    return this.bases.find(b => b.schedule_basis === value)?.label || value;
  }

  loadLabel(load: string): string {
    return load === 'intermodal' ? 'Intermodal' : load.toUpperCase();
  }

  dateRange(row: { effective_start?: string | null; effective_end?: string | null }): string {
    return `${row.effective_start?.slice(0, 10) || 'Any start'} → ${row.effective_end?.slice(0, 10) || 'No end date'}`;
  }

  datePart(value: string | null | undefined, fallback: string): string {
    return value?.slice(0, 10) || fallback;
  }

  sideNameHeader(): string {
    return this.side === 'customer' ? 'Customer Name' : 'Carrier Name';
  }

  sideContactName(row: FscRate): string {
    const uuid = this.side === 'customer' ? row.customer_contact_uuid : row.carrier_contact_uuid;
    return row.contact_organization || uuid || '-';
  }

  rateValue(row: FscRate, load: (typeof LOADS)[number]): string {
    if (rateMode(row, this.side) === 'schedule') return 'Per schedule';
    const prefix = this.side === 'carrier' ? 'cost' : 'chargeable';
    return rateMode(row, this.side) === 'offset'
      ? this.number(row[`${prefix}_${load}_offset_multiplier`]) + ' ×'
      : this.percent(row[`${prefix}_${load}_rate`]);
  }

  rateNotes(row: FscRate): string {
    return row.note || '-';
  }

  scheduleScope(row: FscSchedule): string {
    const contact = row.side === 'carrier' ? row.carrier_contact_uuid : row.customer_contact_uuid;
    const rule =
      row.side === 'carrier'
        ? row.fuel_surcharge_cost_rate_uuid
        : row.fuel_surcharge_chargeable_rate_uuid;
    return rule ? 'Specific rule' : contact ? 'Contact schedule' : 'Shared schedule';
  }

  private errorMessage(error: unknown): string {
    if (error instanceof HttpErrorResponse) {
      if (error.status === 0) return 'The connection failed. Refresh before retrying a save.';
      return (
        error.error?.message ||
        error.error?.error?.replace(/_/g, ' ') ||
        `FSC request failed (${error.status}).`
      );
    }
    return error instanceof Error ? error.message : 'FSC request failed. Please try again.';
  }

  canCreateRuleFromSideRate(row: FscRate): boolean {
    return this.side === 'customer' && this.canEdit && !this.busy && !!row.customer_contact_uuid;
  }

  createRuleFromSideRate(row: FscRate): void {
    if (!this.canCreateRuleFromSideRate(row)) return;
    const customerUuid = row.customer_contact_uuid;
    if (!customerUuid || !this.closeEditor()) return;

    const contact: ContactAutocompleteResult = {
      value: customerUuid,
      label: this.sideContactName(row)
    };
    const state = this.contacts.customer;
    state.contact = contact;
    state.rows = [];
    state.error = '';
    this.tab = 'customer';
    this.contactQuery = contact.label;
    this.contactOptions = [];
    this.contactSearchError = '';
    this.searchOpen = false;
    this.searching = false;
    this.expandedRate = '';
    this.page = 0;
    this.loadRates('customer');
    this.editRate();
  }
}